import { Component, ChangeDetectionStrategy, OnInit, Inject, ChangeDetectorRef, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { EntityDataType, MediaModel } from 'src/app/common/media.model';
import { WalletTemplateModel } from 'src/app/common/wallet-template.model';
import { WalletAndroidComponent } from '../media/wallet-android/wallet-android.component';
import { WalletIphoneComponent } from '../media/wallet-iphone/wallet-iphone.component';
import { lastValueFrom, merge, of, Subscription } from 'rxjs';
import { MediaService } from 'src/app/shared/media.service';
import { Honeycomb } from 'src/app/shared/honeycomb-api/honeycomb-api';
import { AppleWalletPassStyle, GoogleWalletPassStyle } from 'src/app/common/wallet-passstyle-enum';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslationService } from 'src/app/shared/translation.service';
import { Globals } from 'src/app/common/globals';
import { WalletBarcodeType } from 'src/app/common/wallet-barcode-type.enum';
import { startWith, map, distinctUntilChanged, auditTime, catchError } from 'rxjs/operators';
import { WalletUpdateBatchService } from 'src/app/shared/wallet-update-batch.service';
import {
  CreateWalletUpdateBatchRequest,
} from './wallet-update-batch.model';
import { WalletTemplateField } from 'src/app/common/wallet-template-field.model';
import { DataType, WalletAppleField, WalletGoogleField, WalletTemplateDataSource } from 'src/app/common/wallet-template-enums';
import { VoucherService } from 'src/app/shared/voucher.service';
import { VoucherModel, VoucherTemplateModel } from 'src/app/common/transaction-header.model';
import { UserService } from 'src/app/shared/user.service';
import { ta } from 'date-fns/locale';
import { CompanyService } from 'src/app/shared/company.service';
import { CompanyViewModel } from 'src/app/setting/company/company-view.model';
import { DOCUMENT } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { GallerySelectorComponent } from '../gallery/gallery-selector.component';
import { ClubService } from 'src/app/shared/club.service';
import { ClubModel } from 'src/app/common/club.model';
import { ReportGridComponent } from 'src/app/common/report-grid/report-grid.component';
import { PromptDialogComponent } from 'src/app/common/dialog.prompt.component';
import { InfoDialogComponent } from 'src/app/common/dialog.info.component';
import {
  AppleWalletSaveValidationContext,
  isAppleValidationFieldSatisfied,
  SaveValidationFieldKey as AppleSaveValidationFieldKey,
  validateAppleWalletForSave,
} from './apple-wallet-save-validation';
import {
  GoogleSaveValidationFieldKey,
  GoogleWalletSaveValidationContext,
  isGoogleValidationFieldSatisfied,
  validateGoogleWalletForSave,
} from './google-wallet-save-validation';

type SaveValidationFieldKey = AppleSaveValidationFieldKey | GoogleSaveValidationFieldKey;

interface WalletTemplateFieldConfig {
  label: string;
  dataSource: string;
  sourceField: string;
  defaultValueSourceField: string;
  dataType: string;
}

interface FieldGroupConfig {
  key: string;
  label: string;
  entityTypes: string[];
  fields?: WalletTemplateFieldConfig[];
  dynamic?: 'UserAttributes' | 'UserCardAttributes' | 'VoucherAttributes' | 'UserRewards';
}

interface FieldGroup {
  key: string;
  label: string;
  entityTypes: string[];
  fields: WalletTemplateField[];
}

interface BackInfoItem {
  id: number;
  sortOrder: number;
  label: string;
  value: string;
}

export interface WalletNotification {
  id: number;
  title: string;
  message: string;
  clubName: string | null;
  scheduledAt: Date | null;
  createdAt: Date;
  sentAt: Date | null;
  notifiedCount: number | null;
}

@Component({
  selector: 'hc-digital-cards-detail',
  templateUrl: './digital-cards-detail.component.html',
  styleUrls: ['./digital-cards-detail.component.scss'],
  imports: [
    WalletAndroidComponent, WalletIphoneComponent, ReactiveFormsModule, FormsModule,
    MatFormFieldModule, MatSelectModule, MatDatepickerModule, MatNativeDateModule, MatCheckboxModule,
    CommonModule, MatIconModule, MatButtonModule, MatSlideToggleModule,
    MatTabsModule, MatInputModule, MatProgressSpinnerModule, MatDialogModule, MatMenuModule, ReportGridComponent
  ]
})
export class DigitalCardsDetailComponent implements OnInit {
  WalletBarcodeType = WalletBarcodeType;
  GoogleWalletPassStyle = GoogleWalletPassStyle;
  AppleWalletPassStyle = AppleWalletPassStyle;
  EntityDataType = EntityDataType;
  selectedTab = 0;
  public isLoading = true;

  public mediaModel: MediaModel;
  public walletGoogleTemplate: WalletTemplateModel;
  public walletAppleTemplate: WalletTemplateModel;
  public voucherTemplate: Honeycomb.Tenant.Vouchers.IService.Model.VoucherTemplate;

  form : FormGroup;
  private formSyncSub?: Subscription;

  walletStyleGoogleOptions: GoogleWalletPassStyle[] = [
    GoogleWalletPassStyle.Generic,
    GoogleWalletPassStyle.GiftCard,
    GoogleWalletPassStyle.LoyaltyCard,
    GoogleWalletPassStyle.Offer
  ];
    
  walletStyleAppleOptions: number[] = Object
    .keys(AppleWalletPassStyle)
    .filter(k => !isNaN(Number(AppleWalletPassStyle[k as any])))
    .map(k => (AppleWalletPassStyle as any)[k])
    .filter(v => v !== AppleWalletPassStyle.BoardingPass) as number[];
  
  walletBarcodeOptions: number[] = Object
    .keys(WalletBarcodeType)
    .filter(k => !isNaN(Number(WalletBarcodeType[k as any])))
    .map(k => (WalletBarcodeType as any)[k]) as number[];



  public googleImageURL: string;  
  public appleImageURL: string; 
  public googleLogoURL: string; 
  public appleLogoURL: string; 
  
  public googleWalletFields:  WalletTemplateField[] = [];
  public googleField: WalletGoogleField = WalletGoogleField.Undefined;

  public appleWalletFields:  WalletTemplateField[] = [];
  public appleField: WalletAppleField = WalletAppleField.Undefined;
  public appleFieldSlotIndex = 0;

  // Fields for selection — built dynamically from JSON config
  public fieldGroups: FieldGroup[] = [];

  // Back side
  public activeCompany: CompanyViewModel | null = null;
  public googleWalletInfos: BackInfoItem[] = [];
  public appleWalletInfos: BackInfoItem[] = [];
  public editingGoogleInfoIndex: number | null = null;
  public editingAppleInfoIndex: number | null = null;

  walletFields: Record<string,string> = {};

  // ===== Notifications =====
  public showNotifForm: boolean = false;
  public notifForm: FormGroup;

  public clubs: ClubModel[] = [];
  public today: Date = new Date();
  public notificationReportVariables: Record<string, number> = {};

  public scheduleErrorMessage: string | null = null;

  public readonly tabIndexContent = 1;
  public readonly tabIndexAppearance = 0;
  public saveValidationFieldErrors: Record<SaveValidationFieldKey, boolean> = {
    appleBarcodeText: false,
    appleSecondaryOrAuxiliary: false,
    appleImages: false,
    googleImages: false,
    googleProgramName: false,
    googleTopRowHeader: false,
    googleTopRowSubHeader: false,
    googleBarcodeValue: false,
    googleDynamicFields: false,
    googleLocalizedTitle: false,
  };
  public saveValidationTabErrors: Record<number, boolean> = {};
  public saveValidationDialogMessages: string[] = [];

  @ViewChild('saveValidationDialog', { static: true }) saveValidationDialog!: TemplateRef<any>;
  @ViewChild(ReportGridComponent) private notificationReportGrid?: ReportGridComponent;
    
  constructor(
    private route: ActivatedRoute,
    private mediaService: MediaService,
    private fb: FormBuilder,
    private globals: Globals,
    private cd: ChangeDetectorRef,
    private companyService: CompanyService,
    private trans: TranslationService,
    private userService: UserService,
    private batchService: WalletUpdateBatchService,
    private clubService: ClubService,
    public dialog: MatDialog,
    @Inject(DOCUMENT) private document: Document,
    @Inject('WalletTemplateController') private templateController: Honeycomb.Tenant.TemplateEngine.IService.WalletTemplateController,
    @Inject('VoucherTemplatesController') private voucherTemplateService: Honeycomb.Tenant.Vouchers.IService.VoucherTemplatesController,
    @Inject('TagAttributeTypesController') private tagAttributeController: Honeycomb.Tenant.Contact.IService.TagAttributeTypesController,
    @Inject('VoucherAttributeTypesController') private voucherAttributeTypesController: Honeycomb.Tenant.Vouchers.IService.VoucherAttributeTypesController,
  ) {
    this.form = this.fb.group({
      appearance: this.fb.group({
        walletStyleGoogle: [null, Validators.required],
        walletStyleApple: [null, Validators.required],
        walletName: [null, Validators.required],
      }),
      content: this.fb.group({ 
        walletBarcode: [null, Validators.required],
      }),
      back: this.fb.group({
        showCompanyInfo: [null],
      }),
      notifications: this.fb.group({
        enableLocationNotifications: [false],
        locationNotificationMessage: [''],
      }),
    });
    this.notifForm = this.fb.group({
      title: ['', [Validators.required, Validators.maxLength(100)]],
      message: ['', [Validators.required, Validators.maxLength(500)]],
      clubID: [null],
      scheduledAt: [null, Validators.required],
    });
  }



  async ngOnInit(): Promise<void> {
    //set tab from query params
    const tab = Number(this.route.snapshot.queryParamMap.get('tab'));
    this.selectedTab = Number.isNaN(tab) ? 0 : tab;
    //Load media
    var mediaID = await this.getIdAsync(this.route);
    let res = await lastValueFrom(this.mediaService.getDetail(mediaID));
    this.mediaModel = new MediaModel();
    Object.assign(this.mediaModel, res);
    // Apply NG default colors if not set
    if (!this.mediaModel.walletBackgroundColor) { this.mediaModel.walletBackgroundColor = '#0c355e'; }
    if (!this.mediaModel.walletTextColor)       { this.mediaModel.walletTextColor       = '#ffffff'; }
    if (!this.mediaModel.walletLabelColor)      { this.mediaModel.walletLabelColor      = '#ffffff'; }
    //Load wallet template
    await this.loadWalletTemplates();
    //Load VoucherTemplate if exists
    await this.loadVoucherTemplate();
    //Set fields
    this.googleWalletFields = this.walletGoogleTemplate?.fields ?? [];
    this.appleWalletFields = this.normalizeAppleFieldsForCurrentStyle(this.walletAppleTemplate?.fields ?? []).fields;
    //set images
    this.setImages(this.mediaModel);
    //Form Control init
    this.setFormControl();
    this.bindFormToModels();
    //Init save buttons
    this.globals.setDefaultSaveButton(async () => await this.save());
    //Load User || Voucher attributes
    const config = await lastValueFrom(this.mediaService.getResourceConfig('DigitalCardFields'));
    await this.createAllFields(config);
    //Load company info for back side
    try {
      this.companyService.setCompanyID = this.mediaModel.companyID;
      this.activeCompany = await lastValueFrom(this.companyService.getActive());
    } catch(e) {
      console.warn('Could not load company info', e);
    }
    // Init back-side infos from fields.
    const allInfos = this.mediaModel.walletInfos ?? [];
    this.googleWalletInfos = this.buildGoogleBackInfosFromFields(this.googleWalletFields);
    this.appleWalletInfos = this.buildAppleBackInfosFromFields(this.appleWalletFields);

    // Legacy fallback: if field records are missing, read old walletInfos once.
    if (!this.googleWalletInfos.length) {
      this.googleWalletInfos = allInfos
        .filter(i => !!i.infoTextGoogle || (!i.infoTextGoogle && !i.infoText))
        .map((i: any, index: number) => ({
          id: Number(i?.walletInfoID ?? 0),
          sortOrder: Number(i?.sortOrder ?? index + 1),
          label: String(i?.infoName ?? '').trim(),
          value: String(i?.infoTextGoogle ?? '').trim(),
        } as BackInfoItem))
        .filter(info => info.label.length > 0 || info.value.length > 0)
        .sort((a, b) => a.sortOrder - b.sortOrder);
    }

    if (!this.appleWalletInfos.length) {
      this.appleWalletInfos = allInfos
        .filter(i => !!i.infoText)
        .map((i: any, index: number) => ({
          id: Number(i?.walletInfoID ?? 0),
          sortOrder: Number(i?.sortOrder ?? index + 1),
          label: String(i?.infoName ?? '').trim(),
          value: String(i?.infoText ?? '').trim(),
        } as BackInfoItem))
        .filter(info => info.label.length > 0 || info.value.length > 0)
        .sort((a, b) => a.sortOrder - b.sortOrder);
    }
    //Load clubs
    try {
      this.clubs = await lastValueFrom(this.clubService.getList(true));
    } catch (e) {
      console.warn('Could not load clubs', e);
    }
    this.notificationReportVariables = {
      mediumID: Number(this.mediaModel.mediaID),
      mediaID: Number(this.mediaModel.mediaID)
    };
    this.isLoading = false;
    this.cd.detectChanges();
  }

  public async save(): Promise<boolean>{
    if (!this.validateBeforeSave()) {
      return false;
    }

    try {
      // walletInfos are no longer used for card back-side content.
      this.mediaModel.walletInfos = [];

      // Create fields and save wallet templates.
      this.syncWalletFields();

      // Sync geo notification fields from form directly to walletAppleTemplate before saving.
      const geoValues = this.form.get('notifications')?.value;
      if (this.walletAppleTemplate && geoValues) {
        (this.walletAppleTemplate as any).enableLocationNotifications = geoValues.enableLocationNotifications ?? false;
        (this.walletAppleTemplate as any).locationNotificationMessage = geoValues.locationNotificationMessage || null;
      }

      // Final compatibility guards right before persist.
      this.walletAppleTemplate.fields = this.syncAppleBackInfoFields(
        this.ensureAppleStaticFields(this.walletAppleTemplate.fields ?? [])
      );
      this.walletGoogleTemplate.fields = this.syncGoogleBackInfoFields(
        this.ensureGoogleStaticFields(this.walletGoogleTemplate.fields ?? [])
      );

      await this.saveWalletTemplate(this.walletGoogleTemplate);
      await this.saveWalletTemplate(this.walletAppleTemplate);

      // Keep media template references in sync with possibly new template IDs.
      this.mediaModel.walletGoogleTemplateID = this.walletGoogleTemplate?.walletTemplateID ?? this.mediaModel.walletGoogleTemplateID;
      this.mediaModel.walletAppleTemplateID = this.walletAppleTemplate?.walletTemplateID ?? this.mediaModel.walletAppleTemplateID;

      // Save media model after template IDs are resolved.
      await lastValueFrom(this.mediaService.updateItem(this.mediaModel.mediaID.toString(), this.mediaModel));

      // Save VoucherTemplate if exists.
      if (this.voucherTemplate?.voucherTemplateId > 0){
        await lastValueFrom(this.voucherTemplateService.Post(<any>this.voucherTemplate));
      }

      this.globals.navigateBack();
      return true;
    } catch (e) {
      this.dialog.open(InfoDialogComponent, {
        data: this.trans.instant('admin.web.unexpected-error') || 'Saving wallet card failed.'
      });
      return false;
    }
  }

  private async saveWalletTemplate(template: WalletTemplateModel): Promise<void> {
    if (!template) {
      throw new Error('Wallet template is missing.');
    }

    const saved = await lastValueFrom(this.templateController.Post(<any>template));
    if (!saved) {
      throw new Error('Wallet template save failed.');
    }

    if (saved?.walletTemplateID) {
      template.walletTemplateID = saved.walletTemplateID;
    }
  }

  public syncWalletFields(){
    const googleFields = this.ensureGoogleStaticFields(this.googleWalletFields ?? []);
    this.walletGoogleTemplate.fields = this.syncGoogleBackInfoFields(googleFields);
    const appleFields = this.normalizeAppleFieldsForCurrentStyle(this.appleWalletFields ?? []).fields;
    this.walletAppleTemplate.fields = this.syncAppleBackInfoFields(this.ensureAppleStaticFields(appleFields));
  }

  public async getIdAsync(route: ActivatedRoute): Promise<string> {
    return new Promise(async (resolve, reject) => {
        route.params.subscribe({ next: routeParams => resolve(routeParams.id), error: error => reject(error)});
    });
  }

  public async loadWalletTemplates() {
    let templates = await lastValueFrom(this.mediaService.getWalletTemplates());
    this.walletGoogleTemplate = templates.find(
      t => t.target === Honeycomb.Common.Enums.WalletTemplateTarget.google
        && t.walletTemplateID == this.mediaModel.walletGoogleTemplateID
    )!;
    this.walletAppleTemplate = templates.find(
      t => t.target === Honeycomb.Common.Enums.WalletTemplateTarget.apple
        && t.walletTemplateID == this.mediaModel.walletAppleTemplateID
    )!;
    // Fallback: load individually if not found in the list (e.g. freshly created templates)
    if (!this.walletGoogleTemplate && this.mediaModel.walletGoogleTemplateID) {
      this.walletGoogleTemplate = await lastValueFrom(this.templateController.Get(this.mediaModel.walletGoogleTemplateID)) as any;
    }
    if (!this.walletAppleTemplate && this.mediaModel.walletAppleTemplateID) {
      this.walletAppleTemplate = await lastValueFrom(this.templateController.Get(this.mediaModel.walletAppleTemplateID)) as any;
    }
  }

  public async loadVoucherTemplate() {
    if (!this.mediaModel.voucherTemplateID) {
      return;
    }
    this.voucherTemplate = await lastValueFrom(this.voucherTemplateService.Get(this.mediaModel.voucherTemplateID)); //.getVoucherTemplate(this.mediaModel.voucherTemplateID.toString()));
  }

  public setImages(media: MediaModel){
    this.googleImageURL = this.getImage(512, media.walletGoogleImageUID);  
    this.appleImageURL = this.getImage(512, media.walletImageUID); 
    this.googleLogoURL = this.getImage(512, media.walletGoogleLogoUID);
    this.appleLogoURL = this.getImage(512, media.walletLogoUID);
  }

  public async pickImage(type: 'googleLogo' | 'appleLogo' | 'googleImage' | 'appleImage') {
    const dialogRef = this.dialog.open(GallerySelectorComponent);
    const selected = await dialogRef.afterClosed().toPromise();
    if (!selected?.recordUID) { return; }
    switch (type) {
      case 'googleLogo':
        this.mediaModel.walletGoogleLogoUID = selected.recordUID;
        this.googleLogoURL = this.getImage(512, selected.recordUID);
        this.clearResolvedSaveValidationErrors();
        break;
      case 'appleLogo':
        this.mediaModel.walletLogoUID = selected.recordUID;
        this.appleLogoURL = this.getImage(512, selected.recordUID);
        this.clearResolvedSaveValidationErrors();
        break;
      case 'googleImage':
        this.mediaModel.walletGoogleImageUID = selected.recordUID;
        this.googleImageURL = this.getImage(512, selected.recordUID);
        this.clearResolvedSaveValidationErrors();
        break;
      case 'appleImage':
        this.mediaModel.walletImageUID = selected.recordUID;
        this.appleImageURL = this.getImage(512, selected.recordUID);
        this.clearResolvedSaveValidationErrors();
        break;
    }
    if (!this.cd['destroyed']) { this.cd.detectChanges(); }
  }

  ngOnDestroy() {
    this.formSyncSub?.unsubscribe();
  }

  private setFormControl(){
    const initialGoogleStyle = Number(this.walletGoogleTemplate?.walletStyle ?? GoogleWalletPassStyle.Generic);
    this.form.get('appearance.walletStyleGoogle')!.patchValue(initialGoogleStyle);
    const initialAppleStyle = Number(this.walletAppleTemplate?.walletStyle ?? AppleWalletPassStyle.Generic);
    this.form.get('appearance.walletStyleApple')!.patchValue(initialAppleStyle);
    const initialName = String(this.walletGoogleTemplate?.name ?? this.walletAppleTemplate?.name ?? '');
    this.form.get('appearance.walletName')!.patchValue(initialName);
    const initialBarcode = Number(this.mediaModel?.walletBarcodeType ?? WalletBarcodeType.Undefined);
    this.form.get('content.walletBarcode')!.patchValue(initialBarcode);
    this.form.get('back.showCompanyInfo')!.patchValue(this.mediaModel?.walletAddCompanyInfo ?? null);
    this.form.get('notifications.enableLocationNotifications')!.patchValue(this.walletAppleTemplate?.enableLocationNotifications ?? false);
    this.form.get('notifications.locationNotificationMessage')!.patchValue(this.walletAppleTemplate?.locationNotificationMessage ?? '');
  }

  private bindFormToModels() {
    this.formSyncSub = this.form.valueChanges.pipe(
      startWith(this.form.getRawValue()),
      auditTime(0),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    ).subscribe(v => {
      this.walletGoogleTemplate = {
        ...this.walletGoogleTemplate,
        walletStyle: v.appearance?.walletStyleGoogle ?? this.walletGoogleTemplate?.walletStyle,
        name: v.appearance?.walletName ?? this.walletGoogleTemplate?.name
      };
      this.walletAppleTemplate = {
        ...this.walletAppleTemplate,
        walletStyle: v.appearance?.walletStyleApple ?? this.walletAppleTemplate?.walletStyle,
        name: v.appearance?.walletName ?? this.walletAppleTemplate?.name,
        enableLocationNotifications: v.notifications?.enableLocationNotifications ?? this.walletAppleTemplate?.enableLocationNotifications,
        locationNotificationMessage: v.notifications?.locationNotificationMessage ?? this.walletAppleTemplate?.locationNotificationMessage,
      };

      this.mediaModel = Object.assign(
        new MediaModel(),
        this.mediaModel,
        {
          walletBarcodeType: v.content.walletBarcode,
          walletAddCompanyInfo: v.back?.showCompanyInfo ?? this.mediaModel.walletAddCompanyInfo
        }
      );

      this.cd.markForCheck();
    });
  }

  private async createAllFields(cfg: FieldGroupConfig[]) {
    if (!Array.isArray(cfg)) {
      throw new Error(`DigitalCardFields config must be an array, got: ${JSON.stringify(cfg)?.slice(0, 200)}`);
    }
    const groups = cfg;

    for (const groupCfg of groups) {
      const fields: WalletTemplateField[] = [];

      // Static fields defined in the JSON config
      (groupCfg.fields || []).forEach(u => fields.push(this.createField(u)));

      // Dynamic fields loaded from DB
      if (groupCfg.dynamic === 'UserAttributes') {
        const attrs = await lastValueFrom(this.userService.getAttributeTypes());
        attrs.forEach(attr => fields.push(this.createField({
          label: 'admin.web.' + attr.name,
          dataSource: 'UserAttributes',
          sourceField: attr.name,
          defaultValueSourceField: '-',
          dataType: "text"//attr.dataType
        })));
      } else if (groupCfg.dynamic === 'UserCardAttributes') {
        const attrs = await lastValueFrom(this.tagAttributeController.GetList());
        attrs.forEach(attr => fields.push(this.createField({
          label: 'admin.web.' + attr.name,
          dataSource: 'UserCardAttributes',
          sourceField: attr.name,
          defaultValueSourceField: '-',
          dataType: "text"//Honeycomb.Common.Enums.DataType[attr.dataType].toString()
        })));
      } else if (groupCfg.dynamic === 'VoucherAttributes') {
        const attrs = await lastValueFrom(this.voucherAttributeTypesController.GetList());
        attrs.forEach(attr => fields.push(this.createField({
          label: 'admin.web.' + attr.name,
          dataSource: 'VoucherAttributes',
          sourceField: attr.name,
          defaultValueSourceField: '-',
          dataType: "text"//Honeycomb.Common.Enums.DataType[attr.dataType].toString()
        })));
      } else if (groupCfg.dynamic === 'UserRewards') {
        fields.push(this.createField({
          label: 'admin.web.user-rewards',
          dataSource: 'UserRewards',
          sourceField: 'Basic_Value',
          defaultValueSourceField: '0',
          dataType: 'IntegerNumber'
        }));
      }

      this.fieldGroups.push({
        key: groupCfg.key,
        label: groupCfg.label,
        entityTypes: groupCfg.entityTypes || [],
        fields
      });
    }
  }

  public isGroupVisible(group: FieldGroup): boolean {
    if (!group.entityTypes?.length) return true;
    const typeName = EntityDataType[this.mediaModel?.entityDataType];
    return group.entityTypes.includes(typeName);
  }

  private mapEnumValue<T>(value: string | number, enumObj: any, defaultValue: T): T {
    if (!value && value !== 0) return defaultValue;
    const valueStr = String(value).toLowerCase();
    const key = Object.keys(enumObj).find(k => k.toLowerCase() === valueStr);
    return key ? enumObj[key] : defaultValue;
  }

  private createField(cfg: WalletTemplateFieldConfig): WalletTemplateField {
    const item: WalletTemplateField = {
      walletTemplateFieldID: 0,
      walletTemplateID: 0,
      fieldOrder: 0,
      dataType: this.mapEnumValue(cfg.dataType, DataType, DataType.Text),
      dataSource: this.mapEnumValue(cfg.dataSource, WalletTemplateDataSource, WalletTemplateDataSource.None),
      label: cfg.label,
      sourceField: cfg.sourceField,
      labelDataSource: WalletTemplateDataSource.None,
      defaultValueDataSource: WalletTemplateDataSource.None,
      defaultValueSourceField: cfg.defaultValueSourceField,
      defaultValueLabel: cfg.label,
      defaultValueLabelDataSource: WalletTemplateDataSource.None,
      appleField: WalletAppleField.Undefined,
      googleField: WalletGoogleField.Undefined,
      changeMessage: '',
      googleClassVariantField: false,
      created: new Date()
    };
    return item;
  }



  public getImage(size: number, uid?: string | null): string | null {
    // return 'http' + this.globals.GetUrlPrefix() + '/api/gallery/thumbByID/50/'
    //     + uid + '?tenantID=' + this.globals.getTenantID();
    if (!uid) return null;

    const base = this.globals.GetUrlPrefix().replace(/\/+$/, '');
    const id = encodeURIComponent(uid);
    const tenant = encodeURIComponent(this.globals.getTenantID());

    return `${base}/api/gallery/thumbByID/${size}/${id}?tenantID=${tenant}`;
  }

  getImagePath(imgName: string): string {
    const baseUrl = this.document.baseURI;
    return `${baseUrl}img/${imgName}.png`;
  }

  // ===== Back side helpers =====

  // --- Google ---
  addGoogleInfo(): void {
    const n: BackInfoItem = {
      id: 0,
      sortOrder: (this.googleWalletInfos?.length ?? 0) + 1,
      label: '',
      value: '',
    };
    this.googleWalletInfos = [...(this.googleWalletInfos ?? []), n];
    this.editingGoogleInfoIndex = this.googleWalletInfos.length - 1;
    this.cd.markForCheck();
  }

  removeGoogleInfo(index: number): void {
    const updated = [...this.googleWalletInfos];
    updated.splice(index, 1);
    updated.forEach((item, i) => item.sortOrder = i + 1);
    this.googleWalletInfos = updated;
    if (this.editingGoogleInfoIndex === index) this.editingGoogleInfoIndex = null;
    this.cd.markForCheck();
  }

  moveGoogleInfo(index: number, direction: -1 | 1): void {
    const updated = [...this.googleWalletInfos];
    const target = index + direction;
    if (target < 0 || target >= updated.length) return;
    [updated[index], updated[target]] = [updated[target], updated[index]];
    updated.forEach((item, i) => item.sortOrder = i + 1);
    this.googleWalletInfos = updated;
    this.cd.markForCheck();
  }

  startEditGoogleInfo(index: number): void { this.editingGoogleInfoIndex = index; }
  confirmEditGoogleInfo(): void { this.editingGoogleInfoIndex = null; this.cd.markForCheck(); }

  // --- Apple ---
  addAppleInfo(): void {
    const n: BackInfoItem = {
      id: 0,
      sortOrder: (this.appleWalletInfos?.length ?? 0) + 1,
      label: '',
      value: '',
    };
    this.appleWalletInfos = [...(this.appleWalletInfos ?? []), n];
    this.editingAppleInfoIndex = this.appleWalletInfos.length - 1;
    this.cd.markForCheck();
  }

  removeAppleInfo(index: number): void {
    const updated = [...this.appleWalletInfos];
    updated.splice(index, 1);
    updated.forEach((item, i) => item.sortOrder = i + 1);
    this.appleWalletInfos = updated;
    if (this.editingAppleInfoIndex === index) this.editingAppleInfoIndex = null;
    this.cd.markForCheck();
  }

  moveAppleInfo(index: number, direction: -1 | 1): void {
    const updated = [...this.appleWalletInfos];
    const target = index + direction;
    if (target < 0 || target >= updated.length) return;
    [updated[index], updated[target]] = [updated[target], updated[index]];
    updated.forEach((item, i) => item.sortOrder = i + 1);
    this.appleWalletInfos = updated;
    this.cd.markForCheck();
  }

  startEditAppleInfo(index: number): void { this.editingAppleInfoIndex = index; }
  confirmEditAppleInfo(): void { this.editingAppleInfoIndex = null; this.cd.markForCheck(); }

  trackByWalletInfo = (index: number, item: BackInfoItem) => item.id || index;

  addField(field: WalletTemplateField) {
    if (this.googleField != null && this.googleField !== WalletGoogleField.Undefined) {
      const withoutSamePos = (this.googleWalletFields ?? [])
        .filter(f => f.googleField !== this.googleField);

      const nextField: WalletTemplateField = {
        ...field,
        googleField: this.googleField,
        appleField: WalletAppleField.Undefined,
        defaultValueLabel: field.label,
      };

      this.googleWalletFields = [...withoutSamePos, nextField];
      this.syncGoogleBackInfosFromFields();
      this.googleField = WalletGoogleField.Undefined;
    } else if (this.appleField != null && this.appleField !== WalletAppleField.Undefined) {
      const selectedAppleField = this.appleField;
      const selectedSlotIndex = Math.max(0, this.appleFieldSlotIndex || 0);
      const normalized = this.normalizeAppleFieldsForCurrentStyle(this.appleWalletFields ?? []);
      const currentAppleFields = [...normalized.fields];

      if (normalized.changed) {
        this.appleWalletFields = [...normalized.fields];
      }

      const style = this.walletAppleTemplate?.walletStyle;
      const effectiveAppleField = style === AppleWalletPassStyle.StoreCard
        && (selectedAppleField === WalletAppleField.Secondary || selectedAppleField === WalletAppleField.Auxiliary)
        ? WalletAppleField.Secondary
        : selectedAppleField;
      const baseField = {
        ...field,
        appleField: effectiveAppleField,
        googleField: WalletGoogleField.Undefined,
        defaultValueLabel: field.label,
      };

      const fieldsAtSelectedPos = currentAppleFields
        .filter(f => f.appleField === effectiveAppleField)
        .sort((a, b) => (a.fieldOrder ?? 0) - (b.fieldOrder ?? 0));
      const fieldToReplace = fieldsAtSelectedPos.find(f => (f.fieldOrder ?? 0) === selectedSlotIndex) ?? null;

      if (effectiveAppleField === WalletAppleField.LogoText || effectiveAppleField === WalletAppleField.BarcodeValue) {
        const withoutSamePos = currentAppleFields.filter(f => f.appleField !== effectiveAppleField);
        const existingSingleField = fieldsAtSelectedPos[0] ?? null;
        const fixedLabel = effectiveAppleField === WalletAppleField.LogoText ? 'logo text' : 'barcode text';

        this.appleWalletFields = [
          ...withoutSamePos,
          {
            ...baseField,
            fieldOrder: 0,
            label: existingSingleField?.label || fixedLabel,
            defaultValueLabel: existingSingleField?.defaultValueLabel || existingSingleField?.label || fixedLabel,
          }
        ];
        this.appleField = WalletAppleField.Undefined;
        this.appleFieldSlotIndex = 0;
        this.clearResolvedSaveValidationErrors();
        return;
      }

      if ([WalletAppleField.Header, WalletAppleField.Primary].indexOf(effectiveAppleField) >= 0) {
        const withoutSamePos = currentAppleFields.filter(f => f.appleField !== effectiveAppleField);
        this.appleWalletFields = [...withoutSamePos, { ...baseField, fieldOrder: 0 }];
        this.appleField = WalletAppleField.Undefined;
        this.appleFieldSlotIndex = 0;
        this.clearResolvedSaveValidationErrors();
        return;
      }

      if (effectiveAppleField === WalletAppleField.Secondary || effectiveAppleField === WalletAppleField.Auxiliary) {
        if (style === AppleWalletPassStyle.EventTicket) {
          if (fieldToReplace) {
            this.appleWalletFields = currentAppleFields.map(f => f === fieldToReplace ? { ...baseField, fieldOrder: fieldToReplace.fieldOrder ?? 0 } : f);
            this.appleField = WalletAppleField.Undefined;
            this.appleFieldSlotIndex = 0;
            this.clearResolvedSaveValidationErrors();
            return;
          }

          if (fieldsAtSelectedPos.length >= 4) {
            this.appleField = WalletAppleField.Undefined;
            this.appleFieldSlotIndex = 0;
            return;
          }

          this.appleWalletFields = [...currentAppleFields, { ...baseField, fieldOrder: selectedSlotIndex }];
          this.appleField = WalletAppleField.Undefined;
          this.appleFieldSlotIndex = 0;
          this.clearResolvedSaveValidationErrors();
          return;
        }

        if (fieldToReplace) {
          this.appleWalletFields = currentAppleFields.map(f => f === fieldToReplace ? { ...baseField, fieldOrder: fieldToReplace.fieldOrder ?? 0 } : f);
          this.appleField = WalletAppleField.Undefined;
          this.appleFieldSlotIndex = 0;
          this.clearResolvedSaveValidationErrors();
          return;
        }

        const combinedCount = currentAppleFields.filter(f =>
          f.appleField === WalletAppleField.Secondary || f.appleField === WalletAppleField.Auxiliary).length;

        if (combinedCount >= 4) {
          this.appleField = WalletAppleField.Undefined;
          this.appleFieldSlotIndex = 0;
          return;
        }

        this.appleWalletFields = [...currentAppleFields, { ...baseField, fieldOrder: selectedSlotIndex }];
        this.appleField = WalletAppleField.Undefined;
        this.appleFieldSlotIndex = 0;
        this.clearResolvedSaveValidationErrors();
        return;
      }

      const withoutSamePos = currentAppleFields.filter(f => f.appleField !== effectiveAppleField);
      this.appleWalletFields = [...withoutSamePos, baseField];
      this.appleField = WalletAppleField.Undefined;
      this.appleFieldSlotIndex = 0;
      this.clearResolvedSaveValidationErrors();
    }
  }

  onAppleWalletFieldsChanged(fields: WalletTemplateField[]): void {
    this.appleWalletFields = fields ?? [];
    this.syncAppleBackInfosFromFields();
    this.clearResolvedSaveValidationErrors();
    this.cd.markForCheck();
  }

  onGoogleWalletFieldsChanged(fields: WalletTemplateField[]): void {
    this.googleWalletFields = fields ?? [];
    this.syncGoogleBackInfosFromFields();
    this.clearResolvedSaveValidationErrors();
    this.cd.markForCheck();
  }

  hasFieldError(fieldKey: SaveValidationFieldKey): boolean {
    return !!this.saveValidationFieldErrors[fieldKey];
  }

  hasTabError(tabIndex: number): boolean {
    return !!this.saveValidationTabErrors[tabIndex];
  }

  onGoogleFieldSelected(field: WalletGoogleField | null){
    this.googleField = field ?? WalletGoogleField.Undefined;
    this.appleField = WalletAppleField.Undefined;
    this.cd.markForCheck();
  }

  onAppleFieldSelected(selection: { field: WalletAppleField; slotIndex: number } | WalletAppleField | null) {
    if (selection == null) {
      this.appleField = WalletAppleField.Undefined;
      this.appleFieldSlotIndex = 0;
      this.googleField = WalletGoogleField.Undefined;
      this.cd.markForCheck();
      return;
    }

    if (typeof selection === 'number') {
      this.appleField = selection;
      this.appleFieldSlotIndex = 0;
      this.googleField = WalletGoogleField.Undefined;
      this.cd.markForCheck();
      return;
    }

    this.appleField = selection.field;
    this.appleFieldSlotIndex = Math.max(0, selection.slotIndex || 0);
    this.googleField = WalletGoogleField.Undefined;
    this.cd.markForCheck();
  }

  get isSelectingField(): boolean {
    return (this.googleField != null && this.googleField !== WalletGoogleField.Undefined)
        || (this.appleField != null && this.appleField !== WalletAppleField.Undefined);
  }

  trackByItem(index: number, item: any) {
    return item?.id ?? item ?? index;
  }

  private normalizeAppleFieldsForCurrentStyle(fields: WalletTemplateField[]): { fields: WalletTemplateField[]; changed: boolean } {
    const normalized = [...(fields ?? [])];
    let changed = false;

    if (this.walletAppleTemplate?.walletStyle === AppleWalletPassStyle.StoreCard) {
      const storeResult = this.normalizeStoreCardAppleFields(normalized);
      changed = changed || storeResult.changed;
      normalized.splice(0, normalized.length, ...storeResult.fields);
    }

    const positionsToNormalize = [WalletAppleField.Secondary, WalletAppleField.Auxiliary];

    for (const pos of positionsToNormalize) {
      const candidates = normalized
        .map((walletField, index) => ({ walletField, index }))
        .filter(item => item.walletField.appleField === pos)
        .sort((a, b) => {
          const orderDiff = this.normalizeFieldOrder(a.walletField.fieldOrder) - this.normalizeFieldOrder(b.walletField.fieldOrder);
          return orderDiff !== 0 ? orderDiff : a.index - b.index;
        });

      const used = new Set<number>();

      for (const item of candidates) {
        const currentOrder = this.normalizeFieldOrder(item.walletField.fieldOrder);
        let targetOrder = currentOrder;

        if (used.has(targetOrder)) {
          targetOrder = 0;
          while (used.has(targetOrder)) {
            targetOrder++;
          }
        }

        used.add(targetOrder);

        if (item.walletField.fieldOrder !== targetOrder) {
          normalized[item.index] = { ...item.walletField, fieldOrder: targetOrder };
          changed = true;
        }
      }
    }

    return { fields: normalized, changed };
  }

  private normalizeStoreCardAppleFields(fields: WalletTemplateField[]): { fields: WalletTemplateField[]; changed: boolean } {
    const normalized = [...(fields ?? [])];
    const candidates = normalized
      .map((walletField, index) => ({ walletField, index }))
      .filter(item => item.walletField.appleField === WalletAppleField.Secondary || item.walletField.appleField === WalletAppleField.Auxiliary)
      .sort((a, b) => {
        const fieldPosDiff = (a.walletField.appleField ?? 0) - (b.walletField.appleField ?? 0);
        if (fieldPosDiff !== 0) {
          return fieldPosDiff;
        }

        const orderDiff = this.normalizeFieldOrder(a.walletField.fieldOrder) - this.normalizeFieldOrder(b.walletField.fieldOrder);
        return orderDiff !== 0 ? orderDiff : a.index - b.index;
      });

    let changed = false;

    candidates.forEach((item, slotIndex) => {
      if (item.walletField.appleField !== WalletAppleField.Secondary || this.normalizeFieldOrder(item.walletField.fieldOrder) !== slotIndex) {
        normalized[item.index] = {
          ...item.walletField,
          appleField: WalletAppleField.Secondary,
          fieldOrder: slotIndex,
        } as WalletTemplateField;
        changed = true;
      }
    });

    return { fields: normalized, changed };
  }

  private normalizeFieldOrder(value: any): number {
    const num = Number(value);
    if (!Number.isFinite(num)) {
      return 0;
    }

    return Math.max(0, Math.floor(num));
  }

  private ensureGoogleStaticFields(fields: WalletTemplateField[]): WalletTemplateField[] {
    const withoutStaticGoogleFields = (fields ?? []).filter(field =>
      field.googleField !== WalletGoogleField.HexBackgroundColor
      && field.googleField !== WalletGoogleField.HexFontColor
      && field.googleField !== WalletGoogleField.HeroImage
      && field.googleField !== WalletGoogleField.LogoImage
    );

    let normalizedFields = [...withoutStaticGoogleFields];

    if (this.walletGoogleTemplate?.walletStyle === GoogleWalletPassStyle.LoyaltyCard) {
      normalizedFields = normalizedFields.filter(field =>
        field.googleField !== WalletGoogleField.AccountId
        && field.googleField !== WalletGoogleField.AccountName
      );

      normalizedFields.push(this.createGoogleStaticUserCardField(WalletGoogleField.AccountId, 'Number', WalletTemplateDataSource.UserCard));
      normalizedFields.push(this.createGoogleStaticUserCardField(WalletGoogleField.AccountName, 'Text', WalletTemplateDataSource.PrimaryContact));
    }

    return [
      ...normalizedFields,
      this.createGoogleStaticField(WalletGoogleField.HexBackgroundColor, 'WalletBackgroundColor', DataType.Text, ''),
      this.createGoogleStaticField(WalletGoogleField.HexFontColor, 'WalletTextColor', DataType.Text, ''),
      this.createGoogleStaticField(WalletGoogleField.HeroImage, 'WalletGoogleImageUID', DataType.Image, this.mediaModel?.walletGoogleImageUID || ''),
      this.createGoogleStaticField(WalletGoogleField.LogoImage, 'WalletGoogleLogoUID', DataType.Image, this.mediaModel?.walletGoogleLogoUID || ''),
    ];
  }

  private createGoogleStaticField(
    googleField: WalletGoogleField,
    sourceField: string,
    dataType: DataType,
    defaultValueSourceField: string,
  ): WalletTemplateField {
    const existing = (this.walletGoogleTemplate?.fields ?? []).find(f => f.googleField === googleField);

    return {
      ...(existing ?? new WalletTemplateField()),
      walletTemplateFieldID: existing?.walletTemplateFieldID ?? 0,
      walletTemplateID: this.walletGoogleTemplate?.walletTemplateID ?? 0,
      fieldOrder: 0,
      dataType,
      dataSource: WalletTemplateDataSource.Media,
      label: '',
      sourceField,
      labelDataSource: WalletTemplateDataSource.None,
      defaultValueDataSource: WalletTemplateDataSource.None,
      defaultValueSourceField,
      defaultValueLabel: '',
      defaultValueLabelDataSource: WalletTemplateDataSource.None,
      appleField: WalletAppleField.Undefined,
      googleField,
      changeMessage: '',
      created: existing?.created ?? new Date(),
      googleClassVariantField: false,
    } as WalletTemplateField;
  }

  private createGoogleStaticUserCardField(
    googleField: WalletGoogleField,
    sourceField: string,
    datasource: WalletTemplateDataSource,
  ): WalletTemplateField {
    const existing = (this.walletGoogleTemplate?.fields ?? []).find(f => f.googleField === googleField);

    return {
      ...(existing ?? new WalletTemplateField()),
      walletTemplateFieldID: existing?.walletTemplateFieldID ?? 0,
      walletTemplateID: this.walletGoogleTemplate?.walletTemplateID ?? 0,
      fieldOrder: 0,
      dataType: DataType.Text,
      dataSource: datasource,
      label: existing?.label ?? '',
      sourceField,
      labelDataSource: WalletTemplateDataSource.None,
      defaultValueDataSource: WalletTemplateDataSource.None,
      defaultValueSourceField: existing?.defaultValueSourceField || existing?.sourceField || '',
      defaultValueLabel: existing?.defaultValueLabel || '',
      defaultValueLabelDataSource: WalletTemplateDataSource.None,
      appleField: WalletAppleField.Undefined,
      googleField,
      changeMessage: existing?.changeMessage || '',
      created: existing?.created ?? new Date(),
      googleClassVariantField: false,
    } as WalletTemplateField;
  }

  private syncGoogleBackInfosFromFields(): void {
    this.googleWalletInfos = this.buildGoogleBackInfosFromFields(this.googleWalletFields);
  }

  private syncAppleBackInfosFromFields(): void {
    this.appleWalletInfos = this.buildAppleBackInfosFromFields(this.appleWalletFields);
  }

  private buildGoogleBackInfosFromFields(fields: WalletTemplateField[]): BackInfoItem[] {
    return (fields ?? [])
      .filter(field => field.googleField === WalletGoogleField.TextModulesData)
      .sort((a, b) => this.normalizeFieldOrder(a.fieldOrder) - this.normalizeFieldOrder(b.fieldOrder))
      .map((field, index) => ({
        id: field.walletTemplateFieldID ?? 0,
        sortOrder: index + 1,
        label: String(field.defaultValueLabel ?? field.label ?? '').trim(),
        value: String(field.defaultValueSourceField ?? field.sourceField ?? '').trim(),
      } as BackInfoItem))
      .filter(info => info.label.length > 0 || info.value.length > 0);
  }

  private buildAppleBackInfosFromFields(fields: WalletTemplateField[]): BackInfoItem[] {
    return (fields ?? [])
      .filter(field => field.appleField === WalletAppleField.Back)
      .sort((a, b) => this.normalizeFieldOrder(a.fieldOrder) - this.normalizeFieldOrder(b.fieldOrder))
      .map((field, index) => ({
        id: field.walletTemplateFieldID ?? 0,
        sortOrder: index + 1,
        label: String(field.defaultValueLabel ?? field.label ?? '').trim(),
        value: String(field.defaultValueSourceField ?? field.sourceField ?? '').trim(),
      } as BackInfoItem))
      .filter(info => info.label.length > 0 || info.value.length > 0);
  }

  private syncGoogleBackInfoFields(fields: WalletTemplateField[]): WalletTemplateField[] {
    const sourceFields = fields ?? [];
    const existingTextModules = sourceFields
      .filter(field => field.googleField === WalletGoogleField.TextModulesData)
      .sort((a, b) => this.normalizeFieldOrder(a.fieldOrder) - this.normalizeFieldOrder(b.fieldOrder));

    const withoutTextModules = sourceFields.filter(field => field.googleField !== WalletGoogleField.TextModulesData);

    const normalizedBackInfos = (this.googleWalletInfos ?? [])
      .map(info => ({
        label: String(info?.label ?? '').trim(),
        value: String(info?.value ?? '').trim(),
      }))
      .filter(item => item.label.length > 0 || item.value.length > 0);

    const textModuleFields = normalizedBackInfos.map((item, index) => {
      const existing = existingTextModules[index];

      return {
        ...(existing ?? new WalletTemplateField()),
        walletTemplateFieldID: existing?.walletTemplateFieldID ?? 0,
        walletTemplateID: this.walletGoogleTemplate?.walletTemplateID ?? 0,
        fieldOrder: index,
        dataType: DataType.Text,
        dataSource: WalletTemplateDataSource.None,
        label: item.label,
        sourceField: '',
        labelDataSource: WalletTemplateDataSource.None,
        defaultValueDataSource: WalletTemplateDataSource.None,
        defaultValueSourceField: item.value,
        defaultValueLabel: item.label,
        defaultValueLabelDataSource: WalletTemplateDataSource.None,
        appleField: WalletAppleField.Undefined,
        googleField: WalletGoogleField.TextModulesData,
        changeMessage: existing?.changeMessage || '',
        created: existing?.created ?? new Date(),
        googleClassVariantField: false,
      } as WalletTemplateField;
    });

    return [...withoutTextModules, ...textModuleFields];
  }

  private syncAppleBackInfoFields(fields: WalletTemplateField[]): WalletTemplateField[] {
    const sourceFields = fields ?? [];
    const existingBackFields = sourceFields
      .filter(field => field.appleField === WalletAppleField.Back)
      .sort((a, b) => this.normalizeFieldOrder(a.fieldOrder) - this.normalizeFieldOrder(b.fieldOrder));

    const withoutBackFields = sourceFields.filter(field => field.appleField !== WalletAppleField.Back);

    const normalizedBackInfos = (this.appleWalletInfos ?? [])
      .map(info => ({
        label: String(info?.label ?? '').trim(),
        value: String(info?.value ?? '').trim(),
      }))
      .filter(item => item.label.length > 0 || item.value.length > 0);

    const backFields = normalizedBackInfos.map((item, index) => {
      const existing = existingBackFields[index];

      return {
        ...(existing ?? new WalletTemplateField()),
        walletTemplateFieldID: existing?.walletTemplateFieldID ?? 0,
        walletTemplateID: this.walletAppleTemplate?.walletTemplateID ?? 0,
        fieldOrder: index,
        dataType: DataType.Text,
        dataSource: WalletTemplateDataSource.None,
        label: item.label,
        sourceField: '',
        labelDataSource: WalletTemplateDataSource.None,
        defaultValueDataSource: WalletTemplateDataSource.None,
        defaultValueSourceField: item.value,
        defaultValueLabel: item.label,
        defaultValueLabelDataSource: WalletTemplateDataSource.None,
        appleField: WalletAppleField.Back,
        googleField: WalletGoogleField.Undefined,
        changeMessage: existing?.changeMessage || '',
        created: existing?.created ?? new Date(),
        googleClassVariantField: false,
      } as WalletTemplateField;
    });

    return [...withoutBackFields, ...backFields];
  }

  private ensureAppleStaticFields(fields: WalletTemplateField[]): WalletTemplateField[] {
    const withoutStaticColorFields = (fields ?? []).filter(field =>
      field.appleField !== WalletAppleField.BackgroundColor &&
      field.appleField !== WalletAppleField.LabelColor &&
      field.appleField !== WalletAppleField.ForegroundColor
    );

    let normalizedFields = [...withoutStaticColorFields];

    if (this.walletAppleTemplate?.walletStyle === AppleWalletPassStyle.StoreCard
      || this.walletAppleTemplate?.walletStyle === AppleWalletPassStyle.EventTicket) {
      normalizedFields = normalizedFields.filter(field =>
        field.appleField !== WalletAppleField.LogoImage
        && field.appleField !== WalletAppleField.IconImage
        && field.appleField !== WalletAppleField.ThumbnailImage
        && field.appleField !== WalletAppleField.StripImage
      );

      normalizedFields.push(this.createAppleStaticImageField(WalletAppleField.LogoImage, 'WalletLogoUID'));
      normalizedFields.push(this.createAppleStaticImageField(WalletAppleField.StripImage, 'WalletImageUID'));
    }

    return [
      ...normalizedFields,
      this.createAppleStaticColorField(WalletAppleField.BackgroundColor, 'WalletBackgroundColor'),
      this.createAppleStaticColorField(WalletAppleField.LabelColor, 'WalletLabelColor'),
      this.createAppleStaticColorField(WalletAppleField.ForegroundColor, 'WalletTextColor'),
    ];
  }

  private createAppleStaticColorField(appleField: WalletAppleField, sourceField: string): WalletTemplateField {
    const existing = (this.walletAppleTemplate?.fields ?? []).find(f => f.appleField === appleField);

    return {
      ...(existing ?? new WalletTemplateField()),
      walletTemplateFieldID: existing?.walletTemplateFieldID ?? 0,
      walletTemplateID: this.walletAppleTemplate?.walletTemplateID ?? 0,
      fieldOrder: 0,
      dataType: DataType.Text,
      dataSource: WalletTemplateDataSource.Media,
      label: '',
      sourceField,
      labelDataSource: WalletTemplateDataSource.None,
      defaultValueDataSource: WalletTemplateDataSource.None,
      defaultValueSourceField: '',
      defaultValueLabel: '',
      defaultValueLabelDataSource: WalletTemplateDataSource.None,
      appleField,
      googleField: WalletGoogleField.Undefined,
      changeMessage: '',
      created: existing?.created ?? new Date(),
      googleClassVariantField: false,
    } as WalletTemplateField;
  }

  private createAppleStaticImageField(appleField: WalletAppleField, sourceField: string): WalletTemplateField {
    const existing = (this.walletAppleTemplate?.fields ?? []).find(f => f.appleField === appleField);

    return {
      ...(existing ?? new WalletTemplateField()),
      walletTemplateFieldID: existing?.walletTemplateFieldID ?? 0,
      walletTemplateID: this.walletAppleTemplate?.walletTemplateID ?? 0,
      fieldOrder: 0,
      dataType: DataType.Image,
      dataSource: WalletTemplateDataSource.Media,
      label: '',
      sourceField,
      labelDataSource: WalletTemplateDataSource.None,
      defaultValueDataSource: WalletTemplateDataSource.None,
      defaultValueSourceField: '',
      defaultValueLabel: '',
      defaultValueLabelDataSource: WalletTemplateDataSource.None,
      appleField,
      googleField: WalletGoogleField.Undefined,
      changeMessage: '',
      created: existing?.created ?? new Date(),
      googleClassVariantField: false,
    } as WalletTemplateField;
  }

  private validateBeforeSave(): boolean {
    this.resetSaveValidationState();
    const errors = [
      ...validateAppleWalletForSave(this.getAppleValidationContext()),
      ...validateGoogleWalletForSave(this.getGoogleValidationContext()),
    ];

    if (!errors.length) {
      return true;
    }

    errors.forEach(err => {
      this.saveValidationFieldErrors[err.fieldKey] = true;
      this.saveValidationTabErrors[err.tabIndex] = true;
    });

    const firstError = errors[0];
    this.selectedTab = firstError.tabIndex;
    this.saveValidationDialogMessages = errors.map(err => this.trans.instant(err.messageKey) || err.messageKey);
    this.dialog.open(this.saveValidationDialog, { width: '560px', maxWidth: '92vw' });
    this.cd.markForCheck();

    return false;
  }

  private resetSaveValidationState(): void {
    this.saveValidationFieldErrors = {
      appleBarcodeText: false,
      appleSecondaryOrAuxiliary: false,
      appleImages: false,
      googleImages: false,
      googleProgramName: false,
      googleTopRowHeader: false,
      googleTopRowSubHeader: false,
      googleBarcodeValue: false,
      googleDynamicFields: false,
      googleLocalizedTitle: false,
    };
    this.saveValidationTabErrors = {};
  }

  private clearFieldError(fieldKey: SaveValidationFieldKey): void {
    if (!this.saveValidationFieldErrors[fieldKey]) {
      return;
    }

    this.saveValidationFieldErrors[fieldKey] = false;
    this.recomputeSaveValidationTabErrors();
  }

  private clearResolvedSaveValidationErrors(): void {
    const appleContext = this.getAppleValidationContext();
    const googleContext = this.getGoogleValidationContext();
    const keys = Object.keys(this.saveValidationFieldErrors) as SaveValidationFieldKey[];

    for (const key of keys) {
      if (this.isValidationFieldSatisfied(key, appleContext, googleContext)) {
        this.clearFieldError(key);
      }
    }
  }

  private getAppleValidationContext(): AppleWalletSaveValidationContext {
    return {
      appleWalletFields: this.appleWalletFields ?? [],
      mediaModel: this.mediaModel,
      tabIndexContent: this.tabIndexContent,
      tabIndexAppearance: this.tabIndexAppearance,
      normalizeFields: (fields: WalletTemplateField[]) => this.normalizeAppleFieldsForCurrentStyle(fields).fields,
      normalizeFieldOrder: (value: any) => this.normalizeFieldOrder(value),
    };
  }

  private getGoogleValidationContext(): GoogleWalletSaveValidationContext {
    return {
      googleWalletFields: this.googleWalletFields ?? [],
      mediaModel: this.mediaModel,
      walletStyle: this.walletGoogleTemplate?.walletStyle as GoogleWalletPassStyle,
      tabIndexContent: this.tabIndexContent,
      tabIndexAppearance: this.tabIndexAppearance,
    };
  }

  private isValidationFieldSatisfied(
    fieldKey: SaveValidationFieldKey,
    appleContext: AppleWalletSaveValidationContext,
    googleContext: GoogleWalletSaveValidationContext,
  ): boolean {
    if (fieldKey.startsWith('apple')) {
      return isAppleValidationFieldSatisfied(fieldKey as AppleSaveValidationFieldKey, appleContext);
    }

    return isGoogleValidationFieldSatisfied(fieldKey as GoogleSaveValidationFieldKey, googleContext);
  }

  private getTabIndexForValidationField(fieldKey: SaveValidationFieldKey): number {
    return fieldKey === 'appleImages' || fieldKey === 'googleImages'
      ? this.tabIndexAppearance
      : this.tabIndexContent;
  }

  private recomputeSaveValidationTabErrors(): void {
    const nextTabErrors: Record<number, boolean> = {};
    const keys = Object.keys(this.saveValidationFieldErrors) as SaveValidationFieldKey[];

    for (const key of keys) {
      if (!this.saveValidationFieldErrors[key]) {
        continue;
      }

      const tabIndex = this.getTabIndexForValidationField(key);
      nextTabErrors[tabIndex] = true;
    }

    this.saveValidationTabErrors = nextTabErrors;
  }

  // ===== Notification methods =====
  openNotifForm(): void {
    this.notifForm.reset({ clubID: null });
    this.showNotifForm = true;
  }

  cancelNotifForm(): void {
    this.showNotifForm = false;
  }

  async scheduleNotification(): Promise<void> {
    this.scheduleErrorMessage = null;

    if (this.notifForm.invalid) {
      this.notifForm.markAllAsTouched();
      this.cd.markForCheck();
      return;
    }

    const v = this.notifForm.value;

    const req: CreateWalletUpdateBatchRequest = {
      name: v.title,
      walletTemplateID: this.mediaModel.walletAppleTemplateID,
      mediaID: this.mediaModel.mediaID || null,
      processAt: v.scheduledAt ? new Date(v.scheduledAt).toISOString() : new Date().toISOString(),
      message: v.message || null,
      clubID: v.clubID || null
    };

    try {
      await lastValueFrom(this.batchService.createBatch(req));
      this.showNotifForm = false;
      this.notificationReportGrid?.runReport(false, true);
      this.cd.markForCheck();
    } catch (e: any) {
      console.error('Could not schedule notification', e);
      this.scheduleErrorMessage = this.getScheduleErrorTranslationKey(e);
      this.cd.markForCheck();
    }
  }

  public onNotificationReportGridInitialized(reportGrid: ReportGridComponent): void {
    this.notificationReportGrid = reportGrid;
  }

  public onNotificationReportColumnsInitialized(columns: any[]): void {
    if (!Array.isArray(columns) || columns.length === 0) {
      return;
    }

    const first = columns[0];
    if (first) {
      const originalCheckboxSelection = first.checkboxSelection;
      if (originalCheckboxSelection) {
        first.checkboxSelection = (params: any) => {
          const enabledByDefinition = typeof originalCheckboxSelection === 'function'
            ? !!originalCheckboxSelection(params)
            : !!originalCheckboxSelection;
          return enabledByDefinition && this.isNotificationEditableRow(params?.data);
        };
      }
    }

    const statusColumn = this.resolveStatusColumnDef(columns);
    if (!statusColumn) {
      return;
    }

    const originalValueFormatter = statusColumn.valueFormatter;
    statusColumn.valueFormatter = (params: any) => {
      const translated = this.getNotificationStatusText(params?.data);
      if (translated) {
        return translated;
      }

      if (typeof originalValueFormatter === 'function') {
        return originalValueFormatter(params);
      }

      return params?.value;
    };

    const originalComparator = statusColumn.comparator;
    statusColumn.comparator = (valueA: any, valueB: any, nodeA: any, nodeB: any) => {
      const rankA = this.getNotificationStatusSortRank(nodeA?.data);
      const rankB = this.getNotificationStatusSortRank(nodeB?.data);

      if (rankA !== rankB) {
        return rankA - rankB;
      }

      if (typeof originalComparator === 'function') {
        return originalComparator(valueA, valueB, nodeA, nodeB);
      }

      const a = Number(valueA);
      const b = Number(valueB);
      if (Number.isFinite(a) && Number.isFinite(b)) {
        return a - b;
      }

      return String(valueA ?? '').localeCompare(String(valueB ?? ''));
    };
  }

  public onNotificationOptionsDeleteSelected(): void {
    this.onNotificationReportDelete();
  }

  public onNotificationReportDelete(): void {
    const rows = this.getSelectedNotificationRows();
    if (!rows.length) {
      this.dialog.open(InfoDialogComponent, { data: this.trans.instant('admin.web.no-record-selected') });
      return;
    }

    const promptRef = this.dialog.open(PromptDialogComponent, {
      data: { message: `${this.trans.instant('admin.web.delete-question')} (${rows.length})` }
    });

    promptRef.afterClosed().subscribe(async (result) => {
      if (result !== true) {
        return;
      }

      await this.deletePendingNotifications(rows);
    });
  }

  private getSelectedNotificationRows(): Array<{ id: number; editable: boolean }> {
    const nodes = this.notificationReportGrid?.reportGridHandler?.gridApi?.getSelectedNodes?.() ?? [];
    if (!nodes.length) {
      return [];
    }

    return nodes
      .map((n: any) => {
        const rowData = n?.data;
        const id = this.getNotificationIdFromRowData(rowData);
        return {
          id,
          editable: this.isNotificationEditableRow(rowData),
        };
      })
      .filter(r => Number.isFinite(r.id) && r.id > 0);
  }

  private async deletePendingNotifications(rows: Array<{ id: number; editable: boolean }>): Promise<void> {
    let removed = 0;
    let skipped = 0;

    for (const row of rows) {
      if (!row.editable) {
        skipped++;
        continue;
      }

      try {
        await lastValueFrom(this.batchService.deleteBatch(row.id));
        removed++;
      } catch {
        skipped++;
      }
    }

    this.notificationReportGrid?.runReport(false, true);

    if (skipped > 0) {
      const skippedReason = this.trans.instant('admin.web.notification-skip-not-allowed-state');
      const skippedReasonText = skippedReason && skippedReason !== 'admin.web.notification-skip-not-allowed-state'
        ? skippedReason
        : 'Skipped - Not allowed state';
      this.dialog.open(InfoDialogComponent, {
        data: `Deleted: ${removed}. Skipped: ${skipped} (${skippedReasonText}).`
      });
    }
  }

  private isNotificationEditableRow(rowData: any): boolean {
    if (!rowData) {
      return false;
    }

    const statusValue = this.resolveNotificationStatusValue(rowData);
    if (statusValue !== undefined && statusValue !== null && statusValue !== '') {
      const status = Number(statusValue);
      if (Number.isFinite(status)) {
        // Explicit rule from report data: 1 = not processed, 3 = processed.
        return status === 1;
      }
    }

    const startedAt = rowData.startedAt ?? rowData.StartedAt;
    const finishedAt = rowData.finishedAt ?? rowData.FinishedAt;
    return !startedAt && !finishedAt;
  }

  private resolveNotificationStatusValue(rowData: any): any {
    const directStatus = rowData.status ?? rowData.Status ?? rowData.batchStatus;
    if (directStatus !== undefined && directStatus !== null && directStatus !== '') {
      return directStatus;
    }

    const reportColumns = this.notificationReportGrid?.report?.reportColumns ?? [];
    const statusColumn = reportColumns.find(c => {
      const tokens = [
        c?.columnName,
        c?.uniqueColumnName,
        c?.description,
        c?.customDescription,
        c?.queryableColumn?.sqlName,
      ]
        .filter(Boolean)
        .map(v => String(v).toLowerCase());

      return tokens.some(token => token.includes('status'));
    });

    if (!statusColumn?.uniqueColumnName) {
      return undefined;
    }

    return rowData[statusColumn.uniqueColumnName];
  }

  private resolveStatusColumnDef(columns: any[]): any | null {
    if (!Array.isArray(columns) || columns.length === 0) {
      return null;
    }

    return columns.find(c => {
      const tokens = [c?.field, c?.headerName]
        .filter(Boolean)
        .map(v => String(v).toLowerCase());

      return tokens.some(token => token.includes('status'));
    }) ?? null;
  }

  private getNotificationStatusText(rowData: any): string {
    if (!rowData) {
      return '';
    }

    if (this.isPlannedNotificationRow(rowData)) {
      return this.translateStatusText('planned', 'Planned');
    }

    const status = Number(this.resolveNotificationStatusValue(rowData));
    if (!Number.isFinite(status)) {
      return '';
    }

    switch (status) {
      case 1:
        return this.translateStatusText('created', 'Created');
      case 2:
        return this.translateStatusText('being-processed', 'Being processed');
      case 3:
        return this.translateStatusText('processed', 'Processed');
      default:
        return this.translateStatusText('unknown', 'Unknown');
    }
  }

  private getNotificationStatusSortRank(rowData: any): number {
    if (!rowData) {
      return 999;
    }

    if (this.isPlannedNotificationRow(rowData)) {
      return 10;
    }

    const status = Number(this.resolveNotificationStatusValue(rowData));
    if (!Number.isFinite(status)) {
      return 999;
    }

    if (status === 1) {
      return 20; // Created after Planned
    }

    if (status === 2) {
      return 30;
    }

    if (status === 3) {
      return 40;
    }

    return 999;
  }

  private isPlannedNotificationRow(rowData: any): boolean {
    const status = Number(this.resolveNotificationStatusValue(rowData));
    if (!Number.isFinite(status) || status !== 1) {
      return false;
    }

    const processAtRaw = rowData?.processAt ?? rowData?.ProcessAt;
    const processAt = processAtRaw ? Date.parse(String(processAtRaw)) : NaN;
    if (!Number.isFinite(processAt)) {
      return false;
    }

    return processAt > Date.now();
  }

  private translateStatusText(suffix: string, fallback: string): string {
    const key = `admin.web.notification-status-${suffix}`;
    const translated = this.trans.instant(key);
    return translated && translated !== key ? translated : fallback;
  }

  private getNotificationIdFromRowData(rowData: any): number {
    if (!rowData || !this.notificationReportGrid?.report?.reportColumns) {
      return 0;
    }

    const idCol = this.notificationReportGrid.report.reportColumns.find(c => c.rowIDColumn);
    if (!idCol?.uniqueColumnName) {
      return 0;
    }

    const id = Number(rowData[idCol.uniqueColumnName]);
    return Number.isFinite(id) && id > 0 ? id : 0;
  }

  private getScheduleErrorTranslationKey(e: any): string {
    const errorCode = this.resolveErrorCode(e);
    switch (errorCode) {
      case 'WALLET_TEMPLATE_ID_REQUIRED':
        return 'admin.web.notification-error-wallet-template-id-required';
      case 'NO_MEDIA_FOUND_FOR_WALLET_TEMPLATE':
        return 'admin.web.notification-error-no-media-found-for-wallet-template';
      case 'CLUB_HAS_NO_MEMBERS':
        return 'admin.web.notification-error-club-has-no-members';
      case 'COULD_NOT_RESOLVE_CLUB_USER_UIDS':
        return 'admin.web.notification-error-could-not-resolve-club-user-uids';
      case 'NO_USERS_WITH_DOWNLOADED_WALLET_CARD':
        return 'admin.web.notification-error-no-users-with-downloaded-wallet-card';
      default:
        return 'admin.web.notification-error-schedule-failed';
    }
  }

  private resolveErrorCode(e: any): string {
    if (!e) {
      return '';
    }

    if (typeof e === 'string') {
      try {
        return JSON.parse(e);
      } catch {
        return e;
      }
    }

    if (typeof e.error === 'string') {
      try {
        return JSON.parse(e.error);
      } catch {
        return e.error;
      }
    }

    return String(e?.message || e);
  }

}